// @flow
/** @jsx isolateComponent */

import { shuffle } from 'lodash';
import { useEffect, useState } from 'react';

import { Icon as CommonComponentsIcon } from '@zapier/common-components';
import { Zindexes } from '@zapier/theme';
import {
  Button,
  Colors as DesignSystemColors,
  Icon,
  Surface,
  Text,
  Typography,
} from '@zapier/design-system';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

import Options from './Options';

import type { Size } from '@zapier/design-system-marketing-and-paid/src/types';

type OptionsShape = {
  id: string,
  label: string,
};

type Props = {
  /**
   * Dismiss modal when user clicks on the `X`
   */
  dismissPopup: () => void,
  /**
   * Dismiss modal when user clicks on the `Close` button
   */
  dismissPopupOnClose: () => void,
  /**
   * Used to track an event when the popup loads.
   */
  onMount: () => void,
  onSubmit: (selection: string) => void,
  surveySentSuccessfully: boolean,
  size: Size,
};

export const surveyOptions: Array<OptionsShape> = shuffle([
  {
    id: 'search-engine',
    label: 'Search engine',
  },
  {
    id: 'e-mail',
    label: 'Email',
  },
  {
    id: 'social-media',
    label: 'Social media',
  },
  {
    id: 'website-referral',
    label: 'Website Referral',
  },
  {
    id: 'tv',
    label: 'TV',
  },
  {
    id: 'radio',
    label: 'Radio',
  },
  {
    id: 'podcast',
    label: 'Podcast',
  },
  {
    id: 'word-of-mouth',
    label: 'Word of mouth',
  },
  {
    id: 'print',
    label: 'Print',
  },
  {
    id: 'other',
    label: 'Other',
  },
  {
    id: 'video',
    label: 'Video Ad',
  },
]);

const Styles = labelStyles('HomepageSurvey', {
  positioner: {
    bottom: -8,
    borderRadius: '10px',
    boxShadow: '0 0 10px 5px rgba(0, 0, 0, 0.1)',
    position: 'fixed',
    right: '50px',
    zIndex: Zindexes.fixedSurvey,

    '@media screen and (max-width: 565px)': {
      right: '50%',
      transform: 'translateX(50%)',
    },
  },
  root: ({ size, isCollapsed }: { size: Size, isCollapsed: boolean }) => ({
    position: 'relative',
    width: 360,
    padding: '25px 15px 0 15px',
    paddingBottom: size === 'small' && isCollapsed ? 25 : 0,
    ...(size === 'small' ? withModifier(size, { maxWidth: '95vw' }) : null),
  }),
  headerWrapper: {
    paddingTop: '10px',
  },
  controlsWrapper: {
    paddingBottom: 20,
  },
  closeCta: {
    position: 'absolute',
    right: 10,
    top: 10,
    ':focus': {
      boxShadow: `0 0 2px 1px ${DesignSystemColors.ocean}`,
    },
  },
  optionsWrapper: {
    marginTop: 30,
  },
  submitWrapper: {
    backgroundColor: DesignSystemColors.gray5,
    borderTop: `1px solid ${DesignSystemColors.gray20}`,
    marginLeft: -15,
    marginRight: -15,
    padding: '20px 15px 33px 15px',
  },
  successWrapper: {
    alignItems: 'initial',
    display: 'grid',
    justifyItems: 'center',
    minHeight: 190,
  },
  expandButton: {
    ...Typography.smallPrint1,
    color: DesignSystemColors.ocean,
    marginTop: 5,
  },
});

const Survey = ({
  dismissPopup,
  dismissPopupOnClose,
  onSubmit,
  onMount,
  size,
  surveySentSuccessfully,
}: Props) => {
  const [selectedOption, setSelectedOption] = useState('');
  // Begin as collapsed for small only.
  const [isCollapsed, setIsCollapsed] = useState(size === 'small');
  // Once expanded, it may not be collapsed again.
  const expandSurvey = () => {
    setIsCollapsed(false);
  };

  useEffect(() => {
    onMount();
  }, [onMount]);

  function handleSelectOption(e) {
    setSelectedOption(e.target.value);
  }

  function handleSubmit(e) {
    e.preventDefault();

    onSubmit(selectedOption);
  }

  function renderForm() {
    return (
      <form css={Styles.optionsWrapper} onSubmit={handleSubmit}>
        {surveySentSuccessfully ? (
          <div css={Styles.successWrapper}>
            <CommonComponentsIcon
              color={DesignSystemColors.success}
              name="check"
              size={60}
            />
            <Text tag="h2" textAlign="center" type="pageHeader5">
              Thank you
            </Text>
          </div>
        ) : (
          <div css={Styles.controlsWrapper}>
            <Options onChange={handleSelectOption} options={surveyOptions} />
          </div>
        )}
        <div css={Styles.submitWrapper}>
          {surveySentSuccessfully ? (
            <Button
              isFullWidth={true}
              onClick={dismissPopupOnClose}
              size="small"
              type="button"
            >
              Close
            </Button>
          ) : (
            <Button disabled={!selectedOption} isFullWidth={true} size="small">
              Submit
            </Button>
          )}
        </div>
      </form>
    );
  }

  const root = (
    <Surface backgroundColor="white" borderColor="gray20">
      <div css={Styles.root({ size, isCollapsed })}>
        <div css={Styles.headerWrapper}>
          <button
            aria-label="Close the survey"
            css={Styles.closeCta}
            onClick={dismissPopup}
          >
            <Icon color="gray80" name="formX" size={25} />
          </button>
          {!surveySentSuccessfully && (
            <Text fontWeight={700} tag="h2" type="paragraph1Short">
              What brought you to us today?
            </Text>
          )}
        </div>
        {isCollapsed ? (
          <button css={Styles.expandButton} onClick={expandSurvey}>
            (tap to answer)
          </button>
        ) : (
          renderForm()
        )}
      </div>
    </Surface>
  );

  return <div css={Styles.positioner}>{root}</div>;
};

export default Survey;
